import { useCallback, useEffect, useState } from 'react';
import { bankingAPI, reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import {
  Landmark,
  AlertTriangle,
  CheckCircle2,
  ArrowDownLeft,
} from 'lucide-react';

/**
 * Loan drawdown modal (plan §6 / §12).
 *
 * Handles the inbound side of a loan: the tenant receives cash from a lender
 * and wants to record it as a loan drawdown so the loan account balance
 * goes up and the receiving bank account reconciles cleanly. Internally this
 * reuses the internal_transfer mechanism (loan -> receiving account) because
 * a drawdown is effectively a transfer from the loan account.
 *
 * The user picks the target loan account and the server creates a draft
 * match + pending internal_transfer.
 */

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
};

const shortDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

export function LoanDrawdownModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onMatched,
}) {
  const lineAmount = Number(line?.amount ?? 0);

  const [loanAccounts, setLoanAccounts] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [loanAccountsError, setLoanAccountsError] = useState('');
  const [selectedLoanId, setSelectedLoanId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const resetFormState = useCallback(() => {
    setSelectedLoanId('');
    setReference(line?.reference || '');
    setNotes('');
    setError('');
    setDone(null);
    setBusy(false);
  }, [line?.reference]);

  useEffect(() => {
    if (!open) return;
    resetFormState();
  }, [open, line?.id, resetFormState]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingLoans(true);
        setLoanAccountsError('');
        const list = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        const loans = (list || []).filter(
          (a) => a.account_type === 'loan_account' && a.id !== bankAccountId
        );
        setLoanAccounts(loans);
        if (loans.length === 1) setSelectedLoanId(loans[0].id);
      } catch (err) {
        if (cancelled) return;
        setLoanAccountsError(
          err?.response?.data?.error || err.message || 'Could not load loan accounts'
        );
      } finally {
        if (!cancelled) setLoadingLoans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bankAccountId]);

  const canSubmit =
    !!line && !!bankAccountId && !!selectedLoanId && lineAmount > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const result = await reconciliationAPI.createLoanDrawdown({
        statement_line_id: line.id,
        loan_account_id: selectedLoanId,
        reference: reference || null,
        notes: notes || null,
      });
      setDone(result);
      if (onMatched) onMatched(result);
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || 'Could not create drawdown match'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Match loan drawdown">
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <ArrowDownLeft className="h-3.5 w-3.5" />
                Incoming funds (this account)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>{shortDate(line.transaction_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate">{line.description || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div className="font-semibold text-green-700 dark:text-green-400">
                    {formatMoney(lineAmount)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Reference</div>
                  <div className="truncate">{line.reference || '—'}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {lineAmount <= 0 && (
            <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
              <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Drawdowns must be incoming (positive) amounts. Use loan repayment for
                  outgoing payments.
                </span>
              </CardContent>
            </Card>
          )}

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs">
                <div className="flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  Loan drawdown matched (draft).
                </div>
                <div className="mt-2 text-[11px] text-green-800 dark:text-green-200">
                  Reconcile the match to post the loan balance. The lender-side bank
                  transaction will be created on the loan account when you reconcile.
                </div>
                <div className="mt-3">
                  <Button size="sm" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}
              {loanAccountsError && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{loanAccountsError}</span>
                  </CardContent>
                </Card>
              )}

              <div>
                <FormLabel>
                  <span className="inline-flex items-center gap-1">
                    <Landmark className="h-3 w-3" />
                    Source loan account
                  </span>
                </FormLabel>
                <Select
                  value={selectedLoanId}
                  onChange={(e) => setSelectedLoanId(e.target.value)}
                  disabled={loadingLoans}
                >
                  <option value="">
                    {loadingLoans ? 'Loading loans…' : 'Select loan account…'}
                  </option>
                  {loanAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name}
                    </option>
                  ))}
                </Select>
                {!loadingLoans && loanAccounts.length === 0 && !loanAccountsError && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    No loan accounts found. Create one on the banking page first.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. LOAN-2026-001"
                  />
                </div>
                <div>
                  <FormLabel>Notes</FormLabel>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit}>
                  {busy ? 'Saving…' : 'Create drawdown match'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

export default LoanDrawdownModal;
